import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { Stack } from "@mui/material";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { APP_ICONS } from "../../../assets";
import AppLoader from "../../../components/AppLoader";
import PaperContainer from "../../../components/Containers/PaperContainer";
import GlobalButton from "../../../components/CustomButton";
import AddEditServiceCategoryDialog from "../../../components/Dialog/AddEditServiceCategoryDialog";
import CategoryBlockConfirmDialog from "../../../components/Dialog/CategoryBlockConfirmDialog";
import GlobalTable from "../../../components/Tables/GlobalTable";
import TextInput from "../../../components/TextInputs";
import useDebounce from "../../../hooks/useDebounce";
import { setActiveTab } from "../../../lib/slices/activeTabSlice";
import { setDetailIds } from "../../../lib/slices/detailIdsSlice";
import { setSearchKeys } from "../../../lib/slices/searchKeysSlice";
import {
  getAllServiceCategory,
  updateServiceCategory,
} from "../../../services/serviceCategoryApiFn";
import { handleOpenSnackBar } from "../../../utils";

const headerData = [
  "createdAt",
  "name",
  "description",
  // "Status",
  "actions",
];

const tableRows = [
  "created_date",
  "service_category",
  "category_description",
  // "status",
  "actions",
];

function ServiceCategory() {
  const { t } = useTranslation(["catalogue", "common"]);
  const openDialog = useRef(null);
  const dispatch = useDispatch();
  const openConfirmDialog = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [allCategory, setAllCategory] = useState([]);
  const activeTab = useSelector((state) => state.activeTab.tabs);
  const detailId = useSelector((state) => state.detailIdReducer.detailIds);
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
  const { currentLanguage } = useSelector((state) => state.languageReducer);

  const debouncedSearchTerm = useDebounce(searchKeys.serviceName, 1000);

  useEffect(() => {
    fetchAllServiceCategory(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  const fetchAllServiceCategory = async (name) => {
    try {
      setIsLoading(true);
      const response = await getAllServiceCategory(name);
      if (response.data.status === "success") {
        let data = response?.data.data;
        setAllCategory(data);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (params) => {
    openDialog.current.openDialog({
      type: "edit",
      data: params.row,
      getData: fetchAllServiceCategory,
    });
  };

  const handleOpenConfirmDialog = (event, row, index) => {
    openConfirmDialog.current.openDialog({ event, row, index });
  };

  const handleChangeStatus = async (params) => {
    try {
      let payload = {
        status: params.event,
      };
      setIsLoading(true);
      const response = await updateServiceCategory(params.row._id, payload);
      if (response.data.status) {
        openConfirmDialog.current.closeDialog();
        fetchAllServiceCategory(searchKeys.serviceName);
        handleOpenSnackBar("success", response.data.message);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNavigateToDetail = (row) => {
    dispatch(setDetailIds({ ...detailId, serviceCategoryId: row?._id }));
    dispatch(setActiveTab({ ...activeTab, catalogue: "category_detail" }));
  };

  const prepareExportData = () => {
    return allCategory.map((item) => ({
      "Created At": moment(item.createdAt).format("MM-DD-YYYY") || "N/A",
      "Updated At": moment(item.updatedAt).format("MM-DD-YYYY") || "N/A",
      "Name In Romanian": item.name?.ro || "N/A",
      "Name In English": item.name?.en || "N/A",
      "Description In Romanian": item.description?.ro || "N/A",
      "Description In English": item.description?.en || "N/A",
      "Total Services": item.subserviceCount || 0,
    }));
  };

  const renderHeaderAction = () => {
    return (
      <Stack
        gap={1}
        flexGrow={1}
        direction={{ md: "row" }}
        alignItems={"center"}
        justifyContent={"flex-end"}
      >
        {/* <GlobalButton
          btnText={"addNew"}
          handleClickBtn={() => {
            openDialog.current.openDialog({
              type: "add",
              data: null,
              getData: fetchAllServiceCategory,
            });
          }}
          styleProps={{
            width: "auto",
            height: 40,
          }}
        /> */}
        <CSVLink
          data={prepareExportData()}
          filename={"service_categories.csv"}
          className="csv-link"
          target="_blank"
        >
          <GlobalButton
            variant={"export_button"}
            btnText={t("export", { ns: "common" })}
            startIcon={<img src={APP_ICONS.exportBlockIcon} width={20} />}
            styleProps={{
              width: "auto",
              height: 40,
            }}
          />
        </CSVLink>
      </Stack>
    );
  };

  const renderSearchAction = () => {
    return (
      <Stack p={2} direction={{ md: "row" }} bgcolor={"primary.light"}>
        <TextInput
          styleProps={{ width: { xs: "100%", md: "35%", xl: "30%" } }}
          InputStartIcon={<img src={APP_ICONS.searchGreyIcon} width={25} />}
          InputEndIcon={
            searchKeys.serviceName && (
              <CancelOutlinedIcon
                fontSize="small"
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  dispatch(
                    setSearchKeys({
                      ...searchKeys,
                      serviceName: "",
                    })
                  );
                }}
              />
            )
          }
          placeholder={t("searchByName", { ns: "common" })}
          value={searchKeys.serviceName}
          onChange={(e) => {
            dispatch(
              setSearchKeys({
                ...searchKeys,
                serviceName: e.target.value,
              })
            );
          }}
        />
      </Stack>
    );
  };

  return (
    <>
      <AppLoader isVisible={isLoading} />
      <PaperContainer
        title={t("serviceCategoryList")}
        subtitle={t("serviceCategoryDescription")}
        headerActions={renderHeaderAction()}
        searchActions={renderSearchAction()}
      >
        <GlobalTable
          tableHeader={headerData}
          displayRows={tableRows}
          tableData={allCategory}
          showActions={true}
          showDetail={true}
          showEdit={true}
          handleEdit={handleEdit}
          handleChangeStatus={handleOpenConfirmDialog}
          handleNavigateToDetail={handleNavigateToDetail}
        />
      </PaperContainer>
      <AddEditServiceCategoryDialog ref={openDialog} />
      <CategoryBlockConfirmDialog
        ref={openConfirmDialog}
        handleConfirm={handleChangeStatus}
      />
    </>
  );
}

export default ServiceCategory;
